import { z } from 'zod';
import { moneyStringSchema, uuidSchema, nonNegativeMoneySchema } from './common';
import {
  ecomPaymentMethodsConfigSchema,
  payoutDeductionsConfigSchema,
  posCustomFeesConfigSchema,
  posPaymentMethodsConfigSchema,
} from './payouts';
import { storefrontConfigSchema } from './storefront-content';
import { COACHING_FEATURE_TIERS } from '../coaching/features';
import { COACHING_CREDIT_ACTION_KEYS, COACHING_CREDIT_COST_MAX } from '../coaching/credits';
import {
  ASSISTANT_MODELS,
  COACHING_ASTRO_PROVIDERS,
  PRICING_SLOT_SOURCES,
} from '../tenant-features';

/**
 * AI Assistant persona override: everything optional except the id (merged
 * over `DEFAULT_PERSONAS` by id — see `assistant/personas.ts`).
 *
 * EVERY overridable persona field must be listed. This is a `z.object`, so a
 * field missing here is silently stripped from the admin PATCH — which is
 * exactly how tenant personas ended up unable to carry intake questions or
 * starter chips despite the resolver supporting both.
 */
export const assistantPersonaOverrideSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(60).optional(),
  icon: z.string().max(8).optional(),
  /** Uploaded avatar image (R2 object key); emoji `icon` is the fallback. */
  avatarKey: z.string().max(512).optional(),
  tagline: z.string().max(160).optional(),
  instructions: z.string().max(4000).optional(),
  allowAccountTools: z.boolean().optional(),
  allowWebSearch: z.boolean().optional(),
  voiceHint: z.string().max(40).optional(),
  /** false hides the persona from the picker (existing threads keep working). */
  enabled: z.boolean().optional(),
  // Limits mirror `personaIntake()`'s sanitizer, so anything that survives
  // validation also survives resolution.
  intake: z
    .array(
      z.object({
        key: z.string().min(1).max(64),
        question: z.string().min(1).max(200),
        placeholder: z.string().max(120).optional(),
      }),
    )
    .max(8)
    .optional(),
  suggestedQuestions: z.array(z.string().min(1).max(200)).max(8).optional(),
});

/** Merchant feature (M1): one admin-defined registration requirement definition. */
export const merchantRequirementDefSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'key must be lowercase alphanumeric/underscore'),
  label: z.string().min(1).max(120),
  description: z.string().max(400).optional(),
  required: z.boolean(),
  acceptsFile: z.boolean(),
  fileTypes: z.array(z.string().min(1).max(40)).optional(),
});

export const redeemKitSchema = z.object({
  code: z
    .string()
    .min(8)
    .max(32)
    .transform((s) => s.trim().toUpperCase()),
  pin: z.string().regex(/^\d{4,8}$/, 'pin must be 4-8 digits'),
  sponsorCode: z
    .string()
    .min(4)
    .max(32)
    .transform((s) => s.trim().toUpperCase())
    .optional(),
  anchorSlotId: uuidSchema.optional(),
  side: z.enum(['L', 'R']).optional(),
  // Activation terms acceptance — enforced by the route only when the tenant
  // has `require_activation_terms` on.
  termsAccepted: z.boolean().optional(),
  termsVersion: z.number().int().positive().optional(),
});

export type RedeemKitInput = z.infer<typeof redeemKitSchema>;

export const redeemPreviewSchema = z.object({
  code: z
    .string()
    .min(8)
    .max(32)
    .transform((s) => s.trim().toUpperCase()),
  pin: z.string().regex(/^\d{4,8}$/, 'pin must be 4-8 digits'),
  sponsorCode: z
    .string()
    .min(4)
    .max(32)
    .transform((s) => s.trim().toUpperCase())
    .optional(),
});
export type RedeemPreviewInput = z.infer<typeof redeemPreviewSchema>;

// F4: a leader activates a warm lead's slot with a Code+PIN from their own
// vault. No sponsorCode — the sponsor is forced to the caller; the lead userId
// comes from the route param.
export const activateLeadSchema = z.object({
  code: z
    .string()
    .min(8)
    .max(32)
    .transform((s) => s.trim().toUpperCase()),
  pin: z.string().regex(/^\d{4,8}$/, 'pin must be 4-8 digits'),
  anchorSlotId: uuidSchema.optional(),
  side: z.enum(['L', 'R']).optional(),
  // Activation terms acceptance — the leader accepts on the lead's behalf.
  // Enforced by the route only when the tenant has `require_activation_terms` on.
  termsAccepted: z.boolean().optional(),
  termsVersion: z.number().int().positive().optional(),
});
export type ActivateLeadInput = z.infer<typeof activateLeadSchema>;

// Recipient username is the OPTIONAL half of the double-confirm: the unique
// email always identifies the recipient, and a supplied username is checked
// only when that account actually HAS one. It goes unmatched two ways — the
// tenant doesn't collect usernames at all (`require_username_on_signup` off →
// every user's username is NULL), or an individual account has none on a
// tenant that does (a legacy import leaves most rows that way).
// Empty/whitespace normalizes to undefined.
const recipientUsernameSchema = z
  .string()
  .max(64)
  .transform((s) => s.trim() || undefined)
  .optional();

// F4 (extended): a leader spends a Code+PIN from their own vault to create a
// slot for ANY downline member identified by email (plus username as a soft
// double-confirm where the account has one). Allows extra slots for
// already-active members; the slot limit is enforced server-side.
export const redeemForDownlineSchema = activateLeadSchema.extend({
  recipientUsername: recipientUsernameSchema,
  recipientEmail: z
    .string()
    .email()
    .max(320)
    .transform((s) => s.trim()),
});
export type RedeemForDownlineInput = z.infer<typeof redeemForDownlineSchema>;

// Feature E: transfer a vault code to a downline member, identified by email
// (plus username as a soft double-confirm where the account has one).
export const transferCodeSchema = z.object({
  recipientUsername: recipientUsernameSchema,
  recipientEmail: z
    .string()
    .email()
    .max(320)
    .transform((s) => s.trim()),
});
export type TransferCodeInput = z.infer<typeof transferCodeSchema>;

// BF2: member upgrades a slot with a kit code whose usage_mode is upgrade/both.
export const upgradeSlotSchema = z.object({
  code: z
    .string()
    .min(8)
    .max(32)
    .transform((s) => s.trim().toUpperCase()),
  pin: z.string().regex(/^\d{4,8}$/, 'pin must be 4-8 digits'),
});
export type UpgradeSlotInput = z.infer<typeof upgradeSlotSchema>;

// BF2: admin upgrades a slot via a code OR an explicit target membership + type.
export const adminUpgradeSlotSchema = z
  .object({
    code: z
      .string()
      .min(8)
      .max(32)
      .transform((s) => s.trim().toUpperCase())
      .optional(),
    pin: z
      .string()
      .regex(/^\d{4,8}$/)
      .optional(),
    targetMembershipCode: z.string().min(1).max(64).optional(),
    targetSlotType: z.enum(['paid', 'free']).optional(),
  })
  .refine((v) => (!!v.code && !!v.pin) || (!!v.targetMembershipCode && !!v.targetSlotType), {
    message: 'provide code+pin or targetMembershipCode+targetSlotType',
  });
export type AdminUpgradeSlotInput = z.infer<typeof adminUpgradeSlotSchema>;

// BF2: super_admin re-fires the activation/placement comp for an ALREADY-completed
// slot upgrade (e.g. a paid->paid upgrade whose bonus never fired because
// `paid_upgrade_refires_bonuses` was off at the time). Re-uses the upgrade's
// deterministic comp event id, so it is idempotent: it fires exactly once for a
// never-fired upgrade and is a safe no-op if it already fired.
export const refireUpgradeBonusSchema = z.object({
  upgradeHistoryId: z.string().uuid().optional(),
});
export type RefireUpgradeBonusInput = z.infer<typeof refireUpgradeBonusSchema>;

export const tenantFeaturesPatchSchema = z.object({
  kit_codes_transferable: z.boolean().optional(),
  kit_codes_expire_days: z.number().int().min(0).max(36500).nullable().optional(),
  kit_codes_downline_transfer: z.boolean().optional(),
  member_2fa_required: z.boolean().optional(),
  // Without this line the PATCH silently strips the key and the Settings
  // control is a no-op. `tenant-features-patch.test.ts` is the guard.
  account_archive_mode: z.enum(['deactivate', 'erase']).optional(),
  // `inactivity_block_since` is deliberately absent: the PATCH route stamps it
  // server-side on the off → on flip, and this schema strips a client's copy.
  inactivity_block_enabled: z.boolean().optional(),
  inactivity_block_days: z.number().int().min(30).max(3650).optional(),
  gateway_payments_enabled: z.boolean().optional(),
  // Platform/domain license expiration (ISO date "YYYY-MM-DD", super-admin only).
  // Drives the admin renewal-reminder popup within 3 months of expiry. null = unset.
  domain_expires_at: z.string().max(40).nullable().optional(),
  ecom_event_on: z.enum(['payment_approved', 'order_completed']).optional(),
  wallet_orders_charge_shipping: z.boolean().optional(),
  wallet_orders_auto_complete: z.boolean().optional(),
  virtual_instant_pay: z.boolean().optional(),
  unpaid_order_lock_enabled: z.boolean().optional(),
  // Lets a member cancel their own unpaid order (pending/to_pay).
  member_order_cancel_enabled: z.boolean().optional(),
  virtual_checkout_resume_on_login: z.boolean().optional(),
  // Shows the Accounting section (/accounting) in the admin sidebar.
  accounting_enabled: z.boolean().optional(),
  // Accounting v1 — consignment at the till (cashier surfaces + /pos/consignment*).
  consignment_enabled: z.boolean().optional(),
  // Track 2A — stockist consignment (SCN-/SAR-/SRTN-/SCPAY- branch-keyed module).
  stockist_consignment_enabled: z.boolean().optional(),
  // Cash/Bank register — require a funds account on payments/expenses/receipts.
  funds_account_required: z.boolean().optional(),
  // Accounting Phase 2 — house VAT snapshot at checkout (never reprices).
  house_vat_enabled: z.boolean().optional(),
  // Accounting Phase 2 — VAT rate (money-string percent) for 'vatable' house lines.
  house_vat_default_pct: moneyStringSchema.optional(),
  // Phase 4 lean GL — the poster only books documents dated on/after this date; null = no bound.
  accounting_posting_start_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  // Phase 4 lean GL — fiscal-year start month (1–12); drives the Balance Sheet's Retained Earnings / Net Income split.
  accounting_fiscal_year_start_month: z.number().int().min(1).max(12).optional(),
  // R2 — general invoicing: gates CREATION of general billings + the Receivables
  // surfaces (never the GL poster, which keeps booking already-posted docs).
  ar_invoicing_enabled: z.boolean().optional(),
  // R2 — fallback payment_terms.code when neither document nor customer names one.
  ar_default_terms_code: z.string().min(1).max(64).optional(),
  // R2 — credit-limit behaviour at POST: silent / warn / 409.
  ar_credit_limit_mode: z.enum(['off', 'warn', 'block']).optional(),
  // R2 — which product this deployment presents as (admin sidebar only).
  platform_edition: z.enum(['mlm', 'accounting']).optional(),
  // Without this the Settings "Kit Code & PIN reveal" dropdown silently dropped
  // on save (z.object strips unknown keys) — same trap as the fields below.
  kit_code_reveal_on: z.enum(['payment_approved', 'shipped', 'delivered', 'completed']).optional(),
  // Days before an order awaiting receipt confirmation auto-completes; null/0 = off.
  order_auto_complete_days: z.number().int().min(0).max(365).nullable().optional(),
  // Days before an UNPAID order is auto-cancelled; null/0 = off.
  order_auto_cancel_unpaid_days: z.number().int().min(0).max(365).nullable().optional(),
  disabled_plans: z.array(z.string().min(1).max(64)).optional(),
  earnings_breakdown_order: z.array(z.string().min(1).max(64)).optional(),
  earnings_breakdown_rows: z
    .array(
      z.object({
        key: z.string().min(1).max(64),
        mode: z.enum(['always', 'when_earned', 'hidden']),
      }),
    )
    .max(64)
    .optional(),
  wallet_topup_enabled: z.boolean().optional(),
  wallet_topup_brackets: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        minAmount: moneyStringSchema,
        maxAmount: moneyStringSchema.nullable(),
        bonusKind: z.enum(['pct_wallet', 'pct_gc', 'fixed_wallet', 'fixed_gc']),
        value: moneyStringSchema,
        // A gift check program issued once per top-up in this bracket.
        voucherProgramId: z.string().uuid().nullable().optional(),
      }),
    )
    .max(30)
    .optional(),
  distribution_run_only_plans: z.array(z.string().min(1).max(64)).optional(),
  company_auto_maintained_plans: z.array(z.string().min(1).max(64)).optional(),
  // Previously absent from this schema, so the Settings page silently dropped
  // these on save. The plain z.object strips unknown keys — every admin-editable
  // feature MUST be listed here to persist via PATCH.
  daily_caps: z.record(z.number().min(0)).optional(),
  // Same trap: both of these have had Settings toggles since Order-by-Area
  // shipped but were never listed here, so neither could actually be saved
  // from the UI (the integration tests set them with raw jsonb_set).
  order_by_area: z.boolean().optional(),
  stockist_receivables: z.boolean().optional(),
  // Membership tier applied to buyers with no active slot. Null clears it.
  // The PATCH route re-validates a non-null id against `memberships`.
  non_activated_pricing_membership_id: uuidSchema.nullable().optional(),
  // Which slot's price a multi-slot member pays. Pricing only — attribution
  // (buyer_slot_id / PV / commissions) always follows the selected slot.
  pricing_slot_source: z.enum(PRICING_SLOT_SOURCES).optional(),
  auto_place_slots: z.boolean().optional(),
  manual_placement_on_redeem: z.boolean().optional(),
  genealogy_activate_slot: z.boolean().optional(),
  free_slots_earn: z.boolean().optional(),
  package_replicated_link_enabled: z.boolean().optional(),
  total_saved_counter_enabled: z.boolean().optional(),
  cd_orders_pay_upline: z.boolean().optional(),
  cd_stairstep_count_immediately: z.boolean().optional(),
  half_cd_stairstep_count_immediately: z.boolean().optional(),
  cd_binary_mentor_count_directs: z.boolean().optional(),
  cd_binary_placement_count_directs: z.boolean().optional(),
  half_cd_triggers_like_ps: z.boolean().optional(),
  half_cd_debt_plans: z.array(z.string().max(64)).optional(),
  cd_block_payout_in_debt: z.boolean().optional(),
  cd_topup_settles_debt: z.boolean().optional(),
  cd_topup_bonus_treatment: z.enum(['principal_only', 'include_cash_bonus', 'no_bonus']).optional(),
  max_slots_per_member: z.number().int().positive().nullable().optional(),
  slot_code_series: z.object({ enabled: z.boolean() }).optional(),
  paid_upgrade_refires_bonuses: z.boolean().optional(),
  stairstep_live_rankup: z.boolean().optional(),
  stairstep_rankup_bonus: z.boolean().optional(),
  rank_showcase_enabled: z.boolean().optional(),
  payout_deductions: payoutDeductionsConfigSchema.optional(),
  cashout_schedule: z
    .object({
      enabled: z.boolean(),
      daysOfWeek: z.array(z.number().int().min(0).max(6)),
      openHour: z.number().int().min(0).max(24),
      closeHour: z.number().int().min(0).max(24),
    })
    .optional(),
  cashout_require_kyc: z.boolean().optional(),
  lock_cashout_name_to_owner: z.boolean().optional(),
  cashout_contact_autofill: z.enum(['off', 'prefill', 'locked']).optional(),
  manual_payment_admin_email: z
    .object({
      enabled: z.boolean(),
      recipients: z.array(z.string().trim().email().max(320)).max(20),
    })
    .optional(),
  payout_qr_required: z.boolean().optional(),
  unique_cashout_account_per_user: z.boolean().optional(),
  cod_requires_kyc: z.boolean().optional(),
  manual_payment_force_approve: z.boolean().optional(),
  kyc_requirements: z
    .array(
      z.object({
        key: z
          .string()
          .min(1)
          .max(64)
          .regex(/^[a-z0-9_]+$/, 'key must be lowercase letters, digits, or underscores'),
        label: z.string().min(1).max(120),
        description: z.string().max(300).optional(),
        required: z.boolean(),
      }),
    )
    .max(20)
    .optional(),
  cashout_limits: z
    .object({
      perSlotMin: z.number().min(0).nullable(),
      perSlotMax: z.number().min(0).nullable(),
      perMemberMin: z.number().min(0).nullable(),
      perMemberMax: z.number().min(0).nullable(),
    })
    .optional(),
  multiple_cashout_requests_allowed: z.boolean().optional(),
  cashout_multiple_scope: z.enum(['member', 'slot']).optional(),
  cashout_cancellation_allowed: z.boolean().optional(),
  // Defaulted rather than optional per key: the parsed value is written whole
  // into the features blob, so a missing key must resolve to '' here rather
  // than land as `undefined` and print that into a spreadsheet cell.
  payout_export: z
    .object({
      txn_prefix: z.string().trim().max(8).default('PE'),
      description: z.string().trim().max(120).default(''),
      sender_mobile: z.string().trim().max(32).default(''),
      sender_address: z.string().trim().max(200).default(''),
      purpose: z.string().trim().max(120).default(''),
    })
    .optional(),
  ecom_payment_methods: ecomPaymentMethodsConfigSchema.optional(),
  pos_payment_methods: posPaymentMethodsConfigSchema.optional(),
  pos_custom_fees: posCustomFeesConfigSchema.optional(),
  kit_auto_create_slot: z
    // `virtual` defaults rather than being required, so a PATCH from an admin
    // bundle that predates it still validates. `.default(false)` (not bare
    // `.optional()`) keeps the parsed type total, which is what the consuming
    // code reads — and it matches the read side, where `resolveFeatures`
    // deep-merges an absent key to false anyway.
    .object({ ecom: z.boolean(), cashier: z.boolean(), virtual: z.boolean().default(false) })
    .optional(),
  product_code_generation: z.boolean().optional(),
  // BF1: Terms & Privacy. The route auto-bumps the matching version when the
  // text changes, so the admin UI patches text (and may pass an explicit
  // version); see apps/api/src/routes/tenant.ts.
  terms_text: z.string().max(100_000).optional(),
  terms_version: z.number().int().positive().optional(),
  privacy_policy_text: z.string().max(100_000).optional(),
  privacy_policy_version: z.number().int().positive().optional(),
  require_terms_on_signup: z.boolean().optional(),
  require_username_on_signup: z.boolean().optional(),
  registration_fields: z
    .array(
      z.object({
        id: z
          .string()
          .min(1)
          .max(64)
          .regex(/^[a-z0-9_]+$/, 'id must be lowercase letters, digits, or underscores'),
        label: z.string().min(1).max(120),
        type: z.enum(['text', 'file']),
        required: z.boolean(),
        order: z.number().int().min(0),
      }),
    )
    .max(30)
    .optional(),
  member_profile_editable: z
    .object({
      name: z.boolean(),
      phone: z.boolean(),
      profilePicture: z.boolean(),
    })
    .optional(),
  merchant_profile_editable: z
    .object({
      businessName: z.boolean(),
      ownerName: z.boolean(),
      contactNumber: z.boolean(),
      contactEmail: z.boolean(),
      pickupAddress: z.boolean(),
    })
    .optional(),
  allow_login_by_username: z.boolean().optional(),
  // Activation terms — route auto-bumps activation_terms_version when the text
  // changes (mirrors terms_version), see apps/api/src/routes/tenant.ts.
  activation_terms_text: z.string().max(100_000).optional(),
  activation_terms_version: z.number().int().positive().optional(),
  activation_success_message: z.string().max(500).optional(),
  order_success_message: z.string().max(2000).optional(),
  unpaid_orders_dialog_message: z.string().max(2000).optional(),
  require_activation_terms: z.boolean().optional(),
  default_sponsor_user_id: z.string().uuid().nullable().optional(),
  default_sponsor_slot_code: z.string().min(1).max(64).nullable().optional(),
  link_sponsor_override_slot_code: z.string().min(1).max(64).nullable().optional(),
  extra_slots_self_sponsor: z.boolean().optional(),
  extra_slots_use_registration_slot: z.boolean().optional(),
  sponsor_editable_from_replicated_link: z.boolean().optional(),
  redeem_for_downline_direct_only: z.boolean().optional(),
  sponsor_editable_extra_slots: z.boolean().optional(),
  sponsor_editable_self_stack: z.boolean().optional(),
  show_sponsor_on_dashboard: z.boolean().optional(),
  binary_genealogy_node_fields: z
    .object({
      fullName: z.boolean(),
      slotCode: z.boolean(),
      packageAmount: z.boolean(),
      leftPoints: z.boolean(),
      rightPoints: z.boolean(),
      sponsorName: z.boolean(),
      sponsorSlotCode: z.boolean(),
      // Defaulted so a stale admin bundle (predating this key) can still save.
      walletBalance: z.boolean().default(false),
    })
    .optional(),
  show_sponsor_on_redeem: z.boolean().optional(),
  signup_mode: z.enum(['on', 'links_only', 'by_sponsor_only']).optional(),
  public_site_info_only: z.boolean().optional(),
  product_feed_hide_featured_packages: z.boolean().optional(),
  product_feed_hide_featured_products: z.boolean().optional(),
  campaign_page_signup_allowed: z.boolean().optional(),
  member_referral_code_self_set: z.boolean().optional(),
  referral_code_max_changes: z.coerce.number().int().min(0).max(100).optional(),
  replicated_page_enabled: z.boolean().optional(),
  virtual_orders_auto_complete: z.boolean().optional(),
  virtual_orders_split_checkout: z.boolean().optional(),
  virtual_products_open_replicated_page: z.boolean().optional(),
  virtual_products_enabled: z.boolean().optional(),
  materials_enabled: z.boolean().optional(),
  inventory_lots_enabled: z.boolean().optional(),
  lot_pick_order: z.enum(['fifo', 'fefo']).optional(),
  item_locator_enabled: z.boolean().optional(),
  vouchers_enabled: z.boolean().optional(),
  voucher_label: z.string().trim().min(1).max(40).optional(),
  birthday_claim_window_days: z.number().int().min(0).max(31).optional(),
  birthday_requires_kyc: z.boolean().optional(),
  birth_date_field: z.enum(['hidden', 'optional', 'required']).optional(),
  signup_free_slot: z
    .object({ enabled: z.boolean(), membership_id: z.string().uuid().nullable() })
    .optional(),
  pos_topup_enabled: z.boolean().optional(),
  pos_topup_exact_methods_enabled: z.boolean().optional(),
  pos_topup_pin_above: nonNegativeMoneySchema.nullable().optional(),
  member_resources_enabled: z.boolean().optional(),
  member_resources_office_viewer: z.boolean().optional(),
  course_video_max_mb: z.number().int().min(1).max(5000).optional(),
  storage_sweep_enabled: z.boolean().optional(),
  course_embed_hosts: z
    .array(
      z
        .string()
        .min(1)
        .max(253)
        .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i, {
          message: 'Must be a hostname, e.g. vimeo.com',
        }),
    )
    .max(50)
    .optional(),
  storage_delete_grace_days: z.number().int().min(0).max(90).optional(),
  // LMS Phase 2: lapsed-learner nudge window. NOTHING type-forces this line —
  // without it the PATCH silently strips the key and the Settings input is a
  // no-op. `tenant-features-patch.test.ts` is the guard.
  lms_nudge_after_days: z.number().int().min(0).max(365).nullable().optional(),
  pos_course_sale_mode: z.enum(['allow', 'warn', 'block']).optional(),
  // LMS Phase 8. Same warning as above: nothing type-forces these two lines.
  lms_content_rereview: z.enum(['flag', 'delist', 'hold']).optional(),
  lms_content_rereview_scope: z.enum(['content', 'all']).optional(),
  lms_abuse_reporting_enabled: z.boolean().optional(),
  lms_seek_restriction: z.boolean().optional(),
  lms_manual_completion: z.boolean().optional(),
  lms_public_catalog: z.boolean().optional(),
  lms_app_web_link: z.boolean().optional(),
  lms_subscription_model: z.enum(['tenant', 'creator']).optional(),
  lms_subscription_eligibility: z.enum(['activated_slot', 'any_member']).optional(),
  lms_subscription_auto_renew_enabled: z.boolean().optional(),
  lms_subscription_card_recurring: z.boolean().optional(),
  lms_enabled: z.boolean().optional(),
  member_dashboard_mode: z.enum(['mlm', 'learning']).optional(),
  show_team_earnings: z.boolean().optional(),
  gpv_includes_ppv: z.boolean().optional(),
  stairstep_exclude_personal_pv: z.boolean().optional(),
  member_pv_display: z.enum(['off', 'ppv_only', 'ppv_and_gpv']).optional(),
  dropshipping_income: z.boolean().optional(),
  store_wide_dropship_link: z.boolean().optional(),
  virtual_funnel_dropship_attribution: z.boolean().optional(),
  replicated_link_pv_to_owner: z.boolean().optional(),
  replicated_link_ref_encrypted: z.boolean().optional(),
  commissions_on_non_cash: z.boolean().optional(),
  coin_label: z.string().min(1).max(40).optional(),
  coin_rewards: z.record(z.number().min(0)).optional(),
  ai_credit_rewards: z.record(z.number().int().min(0)).optional(),
  ledger_labels: z.record(z.string().max(80)).optional(),
  ledger_source_plan_labels: z.record(z.string().max(80)).optional(),
  // JM-EX9: admin-authored member notification templates. Required-shape so a
  // half-typed body doesn't silently drop.
  notification_templates: z
    .object({
      earnings: z.object({
        enabled: z.boolean(),
        title: z.string().max(200),
        body: z.string().max(400),
      }),
      events: z
        .record(
          z.object({
            enabled: z.boolean(),
            title: z.string().max(200),
            body: z.string().max(400),
          }),
        )
        .optional(),
    })
    .optional(),
  // Merchant feature (M1).
  platform_fee_pct: moneyStringSchema.optional(),
  platform_fee_min_pct: moneyStringSchema.optional(),
  platform_fee_max_pct: moneyStringSchema.optional(),
  merchant_pv_commission_pct: moneyStringSchema.optional(),
  merchant_default_vat_pct: moneyStringSchema.optional(),
  merchant_vat_enabled_default: z.boolean().optional(),
  // Realtime Unilevel defaults. Ceiling is a percent of commission (the pool);
  // default-shares are each that level's percent OF THE POOL (level count =
  // array length). All clamped 0–100 at compute via clampPct.
  merchant_rtu_ceiling_pct: moneyStringSchema.optional(),
  merchant_rtu_default_shares: z.array(moneyStringSchema).max(51).optional(),
  merchant_sales_earn_comp: z.boolean().optional(),
  merchant_requirements: z.array(merchantRequirementDefSchema).optional(),
  merchant_payout_deductions: payoutDeductionsConfigSchema.optional(),
  merchant_payout_schedule: z.enum(['weekly', 'biweekly', 'monthly']).optional(),
  creator_platform_fee_pct: moneyStringSchema.optional(),
  creator_platform_fee_min_pct: moneyStringSchema.optional(),
  creator_platform_fee_max_pct: moneyStringSchema.optional(),
  creator_sales_earn_comp: z.boolean().optional(),
  creator_requirements: z.array(merchantRequirementDefSchema).optional(),
  creator_payout_deductions: payoutDeductionsConfigSchema.optional(),
  // Storefront flash-sale merchandising.
  flash_sale: z
    .object({
      enabled: z.boolean(),
      productIds: z.array(z.string().uuid()).max(50),
      endsAt: z.string().datetime({ offset: true }).nullable(),
    })
    .optional(),
  // Landing "packages": admin-curated, ORDERED product ids for a tenant landing's packages section.
  landing_packages: z
    .object({
      productIds: z.array(z.string().uuid()).max(50),
    })
    .optional(),
  // Featured "packages": admin-curated, ORDERED product ids for the storefront's Featured Packages section.
  featured_packages: z
    .object({
      enabled: z.boolean(),
      productIds: z.array(z.string().uuid()).max(50),
      heading: z.string().max(120).nullable(),
    })
    .optional(),
  // Storefront CMS content (hero, banners, vouchers, footer, etc.). The admin
  // editor sends the FULL block on save; see `storefront-content.ts`.
  storefront: storefrontConfigSchema.optional(),
  // Chat (M-chat): master on/off for buyer↔seller messaging.
  messaging_enabled: z.boolean().optional(),
  // Auto chat-block on spam: flag + trailing window (seconds) + message threshold.
  chat_spam_autoblock_enabled: z.boolean().optional(),
  chat_spam_window_seconds: z.number().int().positive().optional(),
  chat_spam_threshold: z.number().int().positive().optional(),
  // Auto-Ship: master on/off for member auto-ship subscriptions.
  autoship_enabled: z.boolean().optional(),
  member_subscription_enabled: z.boolean().optional(),
  member_subscription: z.object({ price: z.string(), currency: z.enum(['cash', 'gc']) }).optional(),
  // AI Assistant (Phase 1): master flag + operator-private config. Both keys
  // MUST be listed here — the admin Settings save PATCHes the WHOLE draft and
  // z.object silently strips unknown keys (same trap as the fields above).
  assistant_enabled: z.boolean().optional(),
  assistant: z
    .object({
      model: z.enum(ASSISTANT_MODELS),
      daily_cap: z.number().int().min(-1).max(100000),
      monthly_budget_usd: z.string().max(20),
      web_search_enabled: z.boolean(),
      // Recall keys are `.optional()` while their siblings are required, on
      // purpose: a browser still running a pre-recall admin bundle PATCHes the
      // draft without them. Required would 400 that save and block ALL settings
      // edits; optional lets it through and `resolveFeatures` deep-merges the
      // default back (which is `false` — recall fails OFF, the safe direction).
      recall_enabled: z.boolean().optional(),
      recall_idle_minutes: z.number().int().min(5).max(10080).optional(),
      recall_digest_max_age_days: z.number().int().min(1).max(365).optional(),
      personas: z.array(assistantPersonaOverrideSchema).max(50),
      /** Optional for the same stale-bundle reason as the recall keys above. */
      credits_enabled: z.boolean().optional(),
      credits_monthly_free: z.number().int().min(0).max(100000).optional(),
      credits_price: z.string().max(20).optional(),
      home_promotion: z.boolean().optional(),
      knowledge: z.string().max(20000),
    })
    .optional(),
  // Miracles Protocol coaching: master flag + operator-private config. Both keys
  // MUST be listed (the whole-draft admin PATCH + z.object strip trap). Every
  // nested key is optional so a stale admin bundle that predates one still
  // saves; `patchFeatures` deep-merges the partial over the stored blob.
  coaching_enabled: z.boolean().optional(),
  coaching: z
    .object({
      astro_provider: z.enum(COACHING_ASTRO_PROVIDERS).optional(),
      astro_natal_cache_hours: z.number().int().min(1).max(8760).nullable().optional(),
      astro_credit_alert_pct: z.number().int().min(1).max(100).optional(),
      free_daily_messages: z.number().int().min(-1).max(100000).optional(),
      pro_daily_messages: z.number().int().min(-1).max(100000).optional(),
      free_active_goals: z.number().int().min(-1).max(1000).optional(),
      streak_freeze_pro: z.boolean().optional(),
      chart_guidance_pro_only: z.boolean().optional(),
      tools_pro_only: z.boolean().optional(),
      astro_monthly_credits: z.number().int().min(0).max(10_000_000).optional(),
      model: z.enum(ASSISTANT_MODELS).optional(),
      daily_engine_enabled: z.boolean().optional(),
      monthly_budget_usd: z.string().max(20).optional(),
      /** https only, or '' to clear: the coach app hands it to the phone's browser. */
      community_url: z
        .union([z.literal(''), z.string().trim().url().max(500).startsWith('https://')])
        .optional(),
      community_label: z.string().trim().max(40).optional(),
      /**
       * The plan feature table. Unknown keys and tiers are dropped by
       * `resolveCoachingPlanFeatures` on READ rather than trusted, so an older
       * saved shape can never widen access after the catalog changes; this
       * schema only has to keep the structure honest.
       */
      plan_features: z
        .object({
          access: z.record(z.enum(COACHING_FEATURE_TIERS)).optional(),
          limits: z.record(z.record(z.number().int().min(0).max(100000))).optional(),
        })
        .nullable()
        .optional(),
      paid_only: z.boolean().optional(),
      /** The frequency categories, in order; `[]` = the built-ins. Keys are stable identifiers. */
      frequency_categories: z
        .array(
          z.object({
            /** Blank while the admin is still typing the row; the reader drops such rows. */
            key: z
              .string()
              .trim()
              .max(30)
              .regex(/^(?:[a-z0-9][a-z0-9_-]*)?$/, 'a short lowercase key'),
            label: z.string().trim().max(30),
            hue: z.number().int().min(0).max(360).nullable().default(null),
          }),
        )
        .max(30)
        .refine(
          (rows) => {
            const keys = rows.map((r) => r.key).filter(Boolean);
            return new Set(keys).size === keys.length;
          },
          { message: 'duplicate category keys' },
        )
        .optional(),
      /** The content library's categories, in order; `[]` = none. */
      library_categories: z
        .array(
          z.object({
            key: z
              .string()
              .trim()
              .max(30)
              .regex(/^(?:[a-z0-9][a-z0-9_-]*)?$/, 'a short lowercase key'),
            label: z.string().trim().max(30),
          }),
        )
        .max(30)
        .refine(
          (rows) => {
            const keys = rows.map((r) => r.key).filter(Boolean);
            return new Set(keys).size === keys.length;
          },
          { message: 'duplicate category keys' },
        )
        .optional(),
      /** Both names are required together: a blank would print nothing where a plan is named. */
      tier_labels: z
        .object({
          pro: z.string().trim().min(1).max(30),
          elite: z.string().trim().min(1).max(30),
        })
        .optional(),
      /** Unknown action keys are dropped on READ by `creditCost`; bounds keep a typo from locking members out. */
      credit_costs: z
        .record(
          z.enum(COACHING_CREDIT_ACTION_KEYS as [string, ...string[]]),
          z.number().int().min(0).max(COACHING_CREDIT_COST_MAX),
        )
        .optional(),
      usd_php_rate: z.number().min(1).max(1000).optional(),
    })
    .optional(),
});

export type TenantFeaturesPatch = z.infer<typeof tenantFeaturesPatchSchema>;
